// src/index.tsx
import React from "react";
import ReactDOM from "react-dom/client";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import App from "./App";
import SocialsHarness from "./components/socials/SocialsHarness";
import { startStaleClientGuard } from "./lib/staleClientGuard";
import { canonicalizeLegacyAppUrl } from "./lib/appNavigationState";
import { startAppResumeCoordinator } from "./lib/appResumeCoordinator";
import { runtimeEnvironment } from "./lib/runtimeEnvironment";

console.log("Index starting");

// PKT-0481: expose the build identity on the served page itself, alongside the static
// build/version.json stamp. `window.__LN_BUILD_SHA__` in the console answers "which sha
// is this browser running" without bundle-hash archaeology.
(window as unknown as Record<string, unknown>).__LN_BUILD_SHA__ =
  runtimeEnvironment.gitSha;

// Suppress benign ResizeObserver loop warning that CRA's dev overlay
// surfaces as a runtime error. It's a known harmless noise from MUI
// components and doesn't affect functionality.
const ignoreResizeObserverError = (e: ErrorEvent) => {
  if (
    e.message ===
      "ResizeObserver loop completed with undelivered notifications." ||
    e.message === "ResizeObserver loop limit exceeded"
  ) {
    e.stopImmediatePropagation();
  }
};
window.addEventListener("error", ignoreResizeObserverError);

// Navigation preflight must run before React chooses its initial surface.
// The Gmail backend and old bookmarks use `/settings`, while LN Pro's router
// reads `?page=settings`. Canonicalize synchronously to avoid mounting the
// Dashboard and silently dropping the callback outcome.
const canonicalStartupUrl = canonicalizeLegacyAppUrl(window.location.href);
if (canonicalStartupUrl !== window.location.href) {
  window.history.replaceState(window.history.state, "", canonicalStartupUrl);
}

// INCIDENT-002: a browser holding a pre-key-rotation bundle presented a revoked credential and
// the operator was locked out for days with every server-side surface reporting healthy. This
// detects a newer deployed build and publishes an update-ready prompt. App verifies
// registered workspace durability before any operator-approved reload. Fire-and-forget —
// the manifest check itself must never gate startup.
startAppResumeCoordinator();
void startStaleClientGuard();

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes (property hooks override to 24h)
      gcTime: 60 * 60 * 1000, // 1 hour — free memory after unmount
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

const rootElement = document.getElementById("root");

/*
 * PKT-0792 · dev-only harness for the Socials tab, at `?socials-harness`.
 *
 * Gated on a NON-production build as well as the flag, so the branch cannot be
 * reached on the deployed app even if the query string is typed there.
 * Local dev cannot use live data (runtimeEnvironment.ts forbids the live hosts
 * outside production, correctly), so this renders the real components against
 * fixtures. Delete with the packet once the tab ships.
 */
const wantsSocialsHarness =
  runtimeEnvironment.environment !== "production" &&
  new URLSearchParams(window.location.search).has("socials-harness");

if (rootElement) {
  ReactDOM.createRoot(rootElement).render(
    <React.StrictMode>
      <QueryClientProvider client={queryClient}>
        {wantsSocialsHarness ? <SocialsHarness /> : <App />}
        <ReactQueryDevtools initialIsOpen={false} />
      </QueryClientProvider>
    </React.StrictMode>
  );
}
